import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { ModifiedPayeeNames } from '../../../modified-first-parties/models/FirstParties';
import { TextInputModel } from '../../../shared/components/controls/text-input-control/TextInputModel';
import { isoInputFieldErrorMap, receivingAccountHolderErrorMap } from '../../../shared/utilities/Constants';
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { isoFieldsAllowedChars,receivingAccountAllowedChars } from '../../../shared/utilities/regex-patterns';
import { createControl } from '../../utilities/formUtilities';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';

@Component({
  standalone: false,
  selector: 'mm-wire-modified-payee',
  templateUrl: './wire-modified-payee.component.html',
  styleUrls: ['./wire-modified-payee.component.scss']
})
export class WireModifiedPayeeComponent implements OnInit, OnDestroy {
  @Input() parentForm: FormGroup;
  @Input() defaultPayee: ModifiedPayeeNames;
  destroy$: Subject<boolean> = new Subject();
  useSloa$: Observable<boolean>;

  controlNames = {
    name1: 'modifiedPayeeName1',
    name2: 'modifiedPayeeName2',
    isoName: 'isoModifiedPayeeName',
  };
  payeeName1Model: TextInputModel = {
    controlName: this.controlNames.name1,
    label: 'Modified recipient name',
    hasGreyText: false,
    htmlMaxLength: 40,
    allowedChars: new RegExp(receivingAccountAllowedChars),
  };
  payeeName2Model: TextInputModel = {
    ...this.payeeName1Model,
    controlName: this.controlNames.name2,
  };

  payeeIsoNameModel: TextInputModel = {
    controlName: this.controlNames.isoName,
    label: 'Modified recipient name',
    hasGreyText: false,
    htmlMaxLength: 140
  };

  constructor(
    private store: Store
  ) { }

  ngOnInit(): void {
    createControl(
      this.parentForm,
      this.payeeIsoNameModel.controlName,
      this.defaultPayee.name1,
      [
        Validators.required,
        Validators.pattern(isoFieldsAllowedChars)
      ],
      undefined,
      undefined,
      undefined,
      'blur'
    );
    if (!!this.name2) {
      createControl(
        this.parentForm,
        this.payeeName2Model.controlName,
        this.defaultPayee.name2,
        [
          Validators.required,
          CustomValidators.isEmptyString,
          Validators.pattern(receivingAccountAllowedChars),
          CustomValidators.firstCharacterAlphaNumeric,
        ],
        undefined,
        undefined,
        undefined,
        'blur'
      );
    };
  }

  ngOnDestroy(): void {
    Object.keys(this.controlNames).forEach((name) => {
      const names = this.controlNames;
      this.parentForm?.removeControl(
        this.controlNames[name as keyof typeof names]
      );
    });

  }

  get name1() {
    return this.defaultPayee.name1.trim();
  }

  get name2() {
    return this.defaultPayee.name2.trim();
  }

  get isoName() {
    return this.defaultPayee.name1.trim();
  }

  get modifiedName1Control(): UntypedFormControl {
    return this.parentForm?.get(this.controlNames.name1) as UntypedFormControl;
  }

  get modifiedName2Control(): UntypedFormControl {
    return this.parentForm?.get(this.controlNames.name2) as UntypedFormControl;
  }

  get isoModifiedNameControl(): UntypedFormControl {
    return this.parentForm?.get(this.controlNames.isoName) as UntypedFormControl;
  }

  get name1Error(): string {
    const errorMap = receivingAccountHolderErrorMap('Recipient name', 44);
    const errorKeys = Object.keys(this.modifiedName1Control?.errors ?? {});

    return errorKeys.length > 0
      ? errorMap[errorKeys[0] as keyof typeof receivingAccountHolderErrorMap]
      : '';
  }

  get name2Error(): string {
    const errorMap = receivingAccountHolderErrorMap('Recipient name', 44);
    const errorKeys = Object.keys(this.modifiedName2Control?.errors ?? {});

    return errorKeys.length > 0
      ? errorMap[errorKeys[0] as keyof typeof receivingAccountHolderErrorMap]
      : '';
  }

  get isoNameError(): string {
    const errorMap = isoInputFieldErrorMap('Recipient name', 140);
    const errorKeys = Object.keys(this.isoModifiedNameControl?.errors ?? {});

    return errorKeys.length > 0
      ? errorMap[errorKeys[0] as keyof typeof isoInputFieldErrorMap]
      : '';
  }
}
